import type { TFunction } from 'i18next';
import type { ContractTotal } from '../components/ContractTimeline';
import type { Contract, Milestone, ProjectFundingContract } from '../types';
import { combinedAgreementTimeline } from './combinedAgreementTimeline';
import { isReplacedMilestone } from './milestoneHistory';

const amountOf = (value: string | number | null | undefined) =>
  parseFloat(String(value || '0').replace(/[^0-9.-]/g, '')) || 0;

/** Confirmed funding takes precedence over the legacy contract balance fields. */
export const contractPaidInAmount = (contract: Contract, funding?: ProjectFundingContract) =>
  funding?.contractId === contract.id && Number.isFinite(funding.fundedAmountOre)
    ? Math.max(0, funding.fundedAmountOre / 100)
    : contract.paidAmount || 0;

/** What the buyer still has to pay in for a contract (after ROT). */
export const contractOutstandingAmount = (contract: Contract, funding?: ProjectFundingContract) => {
  if (funding?.contractId === contract.id && Number.isFinite(funding.remainingFundingNeedOre)) {
    return Math.max(0, funding.remainingFundingNeedOre / 100);
  }
  const paid = contract.paidAmount || 0;
  return Math.max(0, contract.remainingAmount ?? 0, amountOf(contract.afterRotAmount) - paid);
};

interface ContractFrameTotalsInput {
  contract: Contract;
  /** The contract's own stages. */
  milestones: Milestone[];
  /** Every contract and stage of the project, so an ÄTA chain can be combined. */
  relatedContracts: Contract[];
  relatedMilestones: Milestone[];
  fundingContract?: ProjectFundingContract;
  viewerRole?: 'company' | 'person';
  t: TFunction;
  formatCurrency: (amount: number) => string;
  /** Opens payment for the contract; the "Sätt in" button is shown to the buyer only. */
  onPay?: () => void;
  /** What each stage pays out to the company, in öre, when the server knows it (after fees). */
  payoutOreByMilestone?: Record<string, number | null | undefined>;
}

// Stages that are underway: started, reported, attested or being paid out.
// These are the ones "Nästa utbetalning" (and "Att göra") are about.
const IN_FLIGHT_STATUSES = new Set([
  'in_progress', 'ready_for_attestation', 'seller_attested', 'ready_for_reattestation',
  'jointly_attested', 'payout_processing', 'payout_failed', 'under_resolution', 'disputed', 'conflict'
]);

const stageValue = (milestone: Milestone) => milestone.afterRotAmount ?? milestone.amount ?? 0;

export interface ContractFrameFigures {
  /** Contract sum before ROT. */
  amount: number;
  /** Contract sum after ROT (0 when the contract has none). */
  afterRotTotal: number;
  paidIn: number;
  /** What the buyer still has to pay in (after ROT). */
  outstanding: number;
  fullySigned: boolean;
  /** The stages the project actually runs on, see contractFrameTotals. */
  payoutPlan: Milestone[];
  planTotal: number;
  paidOut: number;
  /** Paid in but not yet paid out. */
  reserved: number;
}

/**
 * The amounts behind a contract frame's key figures, so the project overview
 * can add up the same numbers the frames show.
 */
export function contractFrameFigures({ contract, milestones, relatedContracts, relatedMilestones, fundingContract }: Pick<
  ContractFrameTotalsInput, 'contract' | 'milestones' | 'relatedContracts' | 'relatedMilestones' | 'fundingContract'
>): ContractFrameFigures {
  const amount = amountOf(contract.contractAmount);
  const paidIn = contractPaidInAmount(contract, fundingContract);
  const outstanding = contractOutstandingAmount(contract, fundingContract);
  const fullySigned = !!(contract.companySigned && contract.personSigned);
  const afterRotTotal = amountOf(contract.afterRotAmount);
  const base = { amount, afterRotTotal, paidIn, outstanding, fullySigned };
  if (amount <= 0 || !fullySigned) return { ...base, payoutPlan: [], planTotal: 0, paidOut: 0, reserved: 0 };

  const combinedPlan = contract.versionStatus !== 'superseded'
    ? combinedAgreementTimeline(contract, relatedContracts, relatedMilestones)
    : null;
  // Until activation the previous plan still governs. Dropping all stages
  // selected by a pending amendment falsely makes a partly paid plan look complete.
  const governingContract = combinedPlan?.pending
    ? combinedPlan.history[combinedPlan.history.length - 2]?.contract
    : undefined;
  const governingPlan = governingContract && combinedPlan
    ? combinedAgreementTimeline(governingContract, relatedContracts.filter(item => item.id !== combinedPlan.contract.id), relatedMilestones)
    : combinedPlan;
  const governingMilestones = governingContract
    ? relatedMilestones.filter(milestone => milestone.contractId === governingContract.id)
    : milestones;
  const payoutPlan = (governingPlan?.milestones ?? governingMilestones)
    .filter(milestone => !isReplacedMilestone(milestone) && !governingPlan?.contexts[milestone.id]?.pending);
  const planTotal = payoutPlan.reduce((sum, milestone) => sum + stageValue(milestone), 0);
  const paidOut = payoutPlan.filter(milestone => milestone.deliveryStatus === 'paid')
    .reduce((sum, milestone) => sum + stageValue(milestone), 0);
  const unpaidTotal = Math.max(0, planTotal - paidOut);
  const reserved = outstanding === 0 ? unpaidTotal : Math.max(0, Math.min(unpaidTotal, paidIn - paidOut));
  return { ...base, payoutPlan, planTotal, paidOut, reserved };
}

/**
 * The key figures in a contract frame, shared by the project page and the
 * start page so both show the same numbers for the same contract.
 *
 * Payout figures follow the plan the project actually runs on: for a chain
 * with ÄTA that is the combined plan, minus replaced or removed stages and
 * stages of an ÄTA that is not yet active. Amounts are the buyer's funds
 * (after ROT), the same basis as "Insatta medel".
 */
export function contractFrameTotals({
  contract,
  milestones,
  relatedContracts,
  relatedMilestones,
  fundingContract,
  viewerRole,
  t,
  formatCurrency,
  onPay,
  payoutOreByMilestone
}: ContractFrameTotalsInput): { totals: ContractTotal[]; totalsNote?: string } {
  const figures = contractFrameFigures({ contract, milestones, relatedContracts, relatedMilestones, fundingContract });
  const { amount, afterRotTotal, paidIn, outstanding, fullySigned, payoutPlan, planTotal, paidOut, reserved } = figures;
  if (amount <= 0) return { totals: [] };
  const amountTotal: ContractTotal = {
    key: 'amount', icon: 'credit-card', label: t('combinedTimeline.totalsAmount'), value: formatCurrency(amount),
    detail: afterRotTotal > 0 && afterRotTotal < amount
      ? t('combinedTimeline.totalsAfterRot', { amount: formatCurrency(afterRotTotal) }) : undefined
  };
  if (!fullySigned) return { totals: [amountTotal], totalsNote: t('combinedTimeline.totalsFundingLocked') };

  // Next payout: every stage underway in the plan (parallel branches pay out
  // side by side), else the next one to start. The company sees what it is
  // actually paid (after fees) when the server knows it — the same figure as
  // "Frigör … till ditt företag" in "Att göra"; the buyer sees their funds.
  const unpaid = payoutPlan.filter(milestone => milestone.deliveryStatus !== 'paid');
  const inFlight = unpaid.filter(milestone => IN_FLIGHT_STATUSES.has(milestone.deliveryStatus ?? ''));
  const nextStages = inFlight.length ? inFlight : unpaid.slice(0, 1);
  const netOf = (milestone: Milestone) => {
    const ore = payoutOreByMilestone?.[milestone.id];
    return typeof ore === 'number' ? ore / 100 : null;
  };
  const afterFees = viewerRole === 'company' && nextStages.length > 0 && nextStages.every(milestone => netOf(milestone) !== null);
  const nextValue = (milestone: Milestone) => afterFees ? netOf(milestone)! : stageValue(milestone);
  const nextSum = nextStages.reduce((sum, milestone) => sum + nextValue(milestone), 0);
  const [first, second] = nextStages;
  const nextTotal: ContractTotal | null = !first ? null : {
    key: 'nextPayout', icon: 'arrow-right-circle',
    label: t(afterFees ? 'combinedTimeline.totalsNextPayoutAfterFees' : 'combinedTimeline.totalsNextPayout'),
    value: nextStages.length === 1
      ? t('combinedTimeline.totalsNextPayoutValue', { number: first.milestoneNumber, amount: formatCurrency(nextSum) })
      : nextStages.length === 2
        ? t('combinedTimeline.totalsNextPayoutTwo', { first: first.milestoneNumber, second: second.milestoneNumber, amount: formatCurrency(nextSum) })
        : t('combinedTimeline.totalsNextPayoutMany', {
          first: first.milestoneNumber, second: second.milestoneNumber, more: nextStages.length - 2, amount: formatCurrency(nextSum)
        }),
    detail: nextStages.length > 1 ? t('combinedTimeline.totalsParallel', { count: nextStages.length }) : undefined,
    breakdown: nextStages.length > 1 ? nextStages.map(milestone => ({
      key: milestone.id,
      label: t('combinedTimeline.totalsBreakdownItem', { number: milestone.milestoneNumber, description: milestone.description ?? '' }),
      value: formatCurrency(nextValue(milestone))
    })) : undefined
  };

  return {
    totals: [
      amountTotal,
      // Nothing can have been paid out before the buyer has paid in.
      ...(planTotal > 0 && (paidIn > 0 || paidOut > 0) ? [{
        key: 'paidOut', icon: 'check-circle',
        label: t(viewerRole === 'person' ? 'combinedTimeline.totalsPaidOutPerson' : 'combinedTimeline.totalsPaidOutCompany'),
        value: formatCurrency(paidOut),
        detail: t('combinedTimeline.totalsPaidOutOf', { total: formatCurrency(planTotal) }),
        progress: Math.min(100, (paidOut / planTotal) * 100),
        tone: paidOut >= planTotal ? 'success' as const : undefined
      }] : []),
      ...(reserved > 0 ? [{
        key: 'reserved', icon: 'lock', label: t('combinedTimeline.totalsReserved'),
        value: formatCurrency(reserved), detail: t('combinedTimeline.totalsReservedHint')
      }] : []),
      ...(nextTotal ? [nextTotal] : []),
      ...(outstanding > 0 ? [{
        key: 'outstanding', icon: 'clock', label: t('combinedTimeline.totalsOutstanding'),
        value: formatCurrency(outstanding), tone: 'warning' as const,
        action: viewerRole === 'person' && onPay ? { label: t('combinedTimeline.totalsPay'), onClick: onPay } : undefined
      }] : [])
    ]
  };
}
